import _slicedToArray from "/home/greenhack/tfjs-yolov5-example/node_modules/@babel/runtime/helpers/esm/slicedToArray";

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

/**
 * @license
 * Copyright 2021 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { assert } from '../util_base';
var ARROW = '->';
var ARROW_REGEX = /->/g;
var COMMA = ',';
var ELLIPSIS = '...';
/**
 * Parse an equation for einsum.
 *
 * @param equation The einsum equation (e.g., "ij,jk->ik").
 * @param numTensors Number of tensors provided along with `equation`. Used to
 *   check matching number of input tensors.
 * @returns An object consisting of the following fields:
 *   - allDims: all dimension names as strings.
 *   - summedDims: a list of all dimensions being summed over, as indices to
 *     the elements of `allDims`.
 *   - idDims: indices of the dimensions in each input tensor, as indices to
 *     the elements of `allDims.
 */

export function decodeEinsumEquation(equation, numTensors) {
  equation = equation.replace(/\s/g, ''); // Remove witespace in equation.

  var numArrows = (equation.length - equation.replace(ARROW_REGEX, '').length) / ARROW.length;

  if (numArrows < 1) {
    throw new Error('Equations without an arrow are not supported.');
  } else if (numArrows > 1) {
    throw new Error("Equation must contain exactly one arrow (\"".concat(ARROW, "\")."));
  }

  var _equation$split = equation.split(ARROW),
      _equation$split2 = _slicedToArray(_equation$split, 2),
      inputString = _equation$split2[0],
      outputString = _equation$split2[1];

  assert(inputString.indexOf(ELLIPSIS) === -1, function () {
    return "The ellipsis notation (\"".concat(ELLIPSIS, "\") is not supported yet.");
  });
  var inputTerms = inputString.split(COMMA);
  var numInputs = inputTerms.length;

  if (numTensors !== numInputs) {
    throw new Error("Expected ".concat(numInputs, " input tensors, received ").concat(numTensors));
  }

  if (numInputs > 2) {
    throw new Error('Support for more than 2 input tensors is not implemented yet.');
  }

  var allDims = [];

  var _loop = function _loop(i) {
    var dimName = outputString[i];

    if (!inputTerms.some(function (inputTerm) {
      return inputTerm.indexOf(dimName) !== -1;
    })) {
      throw new Error("Output subscripts contain the label ".concat(dimName, " ") + "not present in the input subscripts.");
    }

    if (allDims.indexOf(dimName) === -1) {
      allDims.push(dimName);
    }
  };

  for (var i = 0; i < outputString.length; ++i) {
    _loop(i);
  }

  for (var _i = 0; _i < inputString.length; ++_i) {
    var dimName = inputString[_i];

    if (allDims.indexOf(dimName) === -1 && dimName !== COMMA) {
      allDims.push(dimName);
    }
  }

  var idDims = new Array(inputTerms.length);

  for (var _i2 = 0; _i2 < numInputs; ++_i2) {
    if (new Set(inputTerms[_i2].split('')).size !== inputTerms[_i2].length) {
      throw new Error("Found duplicate axes in input component ".concat(inputTerms[_i2], ". ") + "Support for duplicate axes in input is not implemented yet.");
    }

    idDims[_i2] = [];

    for (var j = 0; j < inputTerms[_i2].length; ++j) {
      idDims[_i2].push(allDims.indexOf(inputTerms[_i2][j]));
    }
  }

  var numDims = allDims.length; // Number of unique dimensions.

  var numOutDims = outputString.length; // Number of output dimensions.

  var summedDims = []; // Dimensions being summed over.

  for (var _i3 = numOutDims; _i3 < numDims; ++_i3) {
    summedDims.push(_i3);
  }

  return {
    allDims: allDims,
    summedDims: summedDims,
    idDims: idDims
  };
}
/**
 * Get the permutation for a given input tensor.
 *
 * @param nDims Total number of dimension of all tensors involved in the einsum
 *   operation.
 * @param idDims Dimension indices involve in the tensor in question.
 * @returns An object consisting of the following fields:
 *   - permutationIndices: Indices to permute the axes of the tensor with.
 *   - expandDims: Indices to the dimension that need to be expanded from the
 *     tensor after permutation.
 */

export function getEinsumPermutation(nDims, idDims) {
  var permutationIndices = new Array(nDims);
  permutationIndices.fill(-1);

  for (var i = 0; i < idDims.length; ++i) {
    permutationIndices[idDims[i]] = i;
  }

  var expandDims = [];

  for (var _i4 = 0; _i4 < nDims; ++_i4) {
    if (permutationIndices[_i4] === -1) {
      expandDims.push(_i4);
    }
  }

  permutationIndices = permutationIndices.filter(function (d) {
    return d !== -1;
  });
  return {
    permutationIndices: permutationIndices,
    expandDims: expandDims
  };
}
/**
 * Checks that the dimension sizes from different input tensors match the
 * equation.
 */

export function checkEinsumDimSizes(nDims, idDims, tensors) {
  var dimSizes = new Array(nDims);

  var _loop2 = function _loop2(i) {
    var shape = tensors[i].shape;

    var _loop3 = function _loop3(j) {
      if (dimSizes[idDims[i][j]] === undefined) {
        dimSizes[idDims[i][j]] = shape[j];
      } else {
        assert(dimSizes[idDims[i][j]] === shape[j], function () {
          return "Expected dimension ".concat(dimSizes[idDims[i][j]], " at axis ").concat(j, " ") + "of input shaped ".concat(JSON.stringify(shape), ", ") + "but got dimension ".concat(shape[j]);
        });
      }
    };

    for (var j = 0; j < idDims[i].length; ++j) {
      _loop3(j);
    }
  };

  for (var i = 0; i < tensors.length; ++i) {
    _loop2(i);
  }
}
/**
 * Gets path of computation for einsum.
 *
 * @param summedDims indices to the dimensions being summed over.
 * @param idDims A look up table for the dimensions present in each input
 *     tensor. Each consituent array contains indices for the dimensions in the
 *     corresponding input tensor.
 *
 * @return A map with two fields:
 *   - path: The path of computation, with each element indicating the dimension
 *     being summed over after the element-wise multiplication in that step.
 *   - steps: With the same length as `path`. Each element contains the indices
 *     to the input tensors being used for element-wise multiplication in the
 *     corresponding step.
 */

export function getEinsumComputePath(summedDims, idDims) {
  var path = summedDims;
  var steps = [];
  var nSteps = 0;

  if (summedDims.length === 0) {
    // Einsum that involes no summing: e.g., transpose and outer product.
    path.push(-1);
  }

  nSteps = summedDims.length + 1;

  for (var i = 0; i < nSteps; ++i) {
    steps.push([]);
  }

  var computedTermIndices = [];

  for (var _i5 = 0; _i5 < path.length; ++_i5) {
    var summedDim = path[_i5];
    var termIndices = findTermsWithDim(idDims, summedDim);

    var _iterator = _createForOfIteratorHelper(termIndices),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var termIndex = _step.value;

        if (computedTermIndices.indexOf(termIndex) === -1) {
          steps[_i5].push(termIndex);

          computedTermIndices.push(termIndex);
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }

  return {
    path: path,
    steps: steps
  };
}
/** Determines if an axes permutation is the identity permutation. */

export function isIdentityPermutation(perm) {
  return perm.every(function (dim, index) {
    return dim === index;
  });
}

function findTermsWithDim(idDims, dim) {
  var termIndices = [];

  for (var i = 0; i < idDims.length; ++i) {
    if (idDims[i].length === 0 || idDims[i].indexOf(dim) !== -1 || dim === -1) {
      termIndices.push(i);
    }
  }

  return termIndices;
}